import { useEffect, useMemo, useState } from 'react';
import { parseMapAreasFromWork, parseMapFeatures } from '../utils/mapAreaParser';
import type { MapArea, MapFeatures } from '../types/MapArea.types';

export interface UseMapAreasResult {
  areas: MapArea[];
  /**
   * Non-interactive features (start points, special zones, no-go/glass). Empty unless the map is
   * georeferenced (config `Resolution` + image dimensions) and its `work`/`function` JSON is
   * available (inline, or via the presigned-URL fallback).
   */
  features: MapFeatures;
  isLoading: boolean;
}

export interface UseMapAreasParams {
  mapWork?: string | null;
  mapWorkUrl?: string | null;
  imageWidthPx?: number | null;
  imageHeightPx?: number | null;
  mapConfig?: string | null;
  mapFunction?: string | null;
  mapFunctionUrl?: string | null;
}

interface FetchTextState {
  text: string | null;
  isLoading: boolean;
}

const IDLE: FetchTextState = { text: null, isLoading: false };

/**
 * Fetches `url` as text when `enabled`, exposing `{ text, isLoading }`. Resets to idle whenever
 * `enabled` goes false or `url` changes; a failed/aborted fetch resolves to `text: null`.
 */
const useFetchedText = (url: string | null | undefined, enabled: boolean): FetchTextState => {
  const [state, setState] = useState<FetchTextState>(IDLE);

  useEffect(() => {
    if (!enabled || !url) {
      setState(IDLE);
      return undefined;
    }

    let isCancelled = false;
    setState({ text: null, isLoading: true });

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Fetch failed with status ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        if (!isCancelled) {
          setState({ text, isLoading: false });
        }
      })
      .catch(error => {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('useMapAreas: failed to fetch map JSON', url, error);
          setState(IDLE);
        }
      });

    return (): void => {
      isCancelled = true;
    };
  }, [url, enabled]);

  return state;
};

/**
 * Areas and non-interactive features for a CV50 map. Falls back to fetching mapWorkUrl/
 * mapFunctionUrl when the inline JSON is missing/empty. See utils/mapAreaParser.ts.
 */
export const useMapAreas = ({
  mapWork,
  mapWorkUrl,
  imageWidthPx,
  imageHeightPx,
  mapConfig,
  mapFunction,
  mapFunctionUrl,
}: UseMapAreasParams): UseMapAreasResult => {
  const inlineAreas = useMemo(
    () => parseMapAreasFromWork(mapWork, imageWidthPx, imageHeightPx, mapConfig),
    [mapWork, imageWidthPx, imageHeightPx, mapConfig]
  );

  const fetchedWork = useFetchedText(mapWorkUrl, inlineAreas.length === 0);
  const fetchedFunction = useFetchedText(mapFunctionUrl, !mapFunction);

  const effectiveWork = mapWork || fetchedWork.text;
  const effectiveFunction = mapFunction || fetchedFunction.text;

  const areas = useMemo(() => {
    if (inlineAreas.length > 0) {
      return inlineAreas;
    }
    return parseMapAreasFromWork(fetchedWork.text, imageWidthPx, imageHeightPx, mapConfig);
  }, [inlineAreas, fetchedWork.text, imageWidthPx, imageHeightPx, mapConfig]);

  const features = useMemo(
    () => parseMapFeatures(effectiveWork, effectiveFunction, imageWidthPx, imageHeightPx, mapConfig),
    [effectiveWork, effectiveFunction, imageWidthPx, imageHeightPx, mapConfig]
  );

  return { areas, features, isLoading: fetchedWork.isLoading || fetchedFunction.isLoading };
};
